import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Row, Col, Form } from "antd";
import { InputItem, DatePickerItem } from "../../../components/forms/FormItems";
import {
  SettingsDataSelect,
  EmployeeEffectiveOrderSelect,
} from "../../../components/forms/AutoCompleteFormItem";
import FormActions from "../../../components/forms/FormActions";
import ChildrenAnimationWrapper from "../../../components/animations/ChildrenAnimationWrapper";
import AnimationChildWrapper from "../../../components/animations/AnimationChildWrapper";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { loadSalaryLines, updateRecord } from "../salarySlice";
import { isAllowedFeature } from "../../organization/organizationSlice";
import { CloseOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import {
  RootState,
  ThunkActionResponse,
  EditSalaryLineModalProps,
  SalaryEditFormValues,
  RouteParams,
} from "../types";

const { Title } = Typography;

// Constants
const FORM_LAYOUT = { labelCol: { span: 6 }, wrapperCol: { span: 18 } };
const MODAL_STYLES = {
  wrapper: { height: "90vh" },
  body: { padding: 0 },
  header: { height: "40px" },
  title: {
    fontSize: "30px",
    fontWeight: 500,
    lineHeight: 1.27,
    color: "rgba(0, 0, 0, 0.85)",
    margin: "13px 0 40px 0",
    textAlign: "center",
  },
  form: { padding: "0 128px", width: "650px" },
} as const;

// Utility functions
const getInitialFormValues = (
  salaryLine: SalaryEditFormValues
): SalaryEditFormValues => ({
  ...salaryLine,
  unit_count:
    salaryLine?.symbol?.type === 1
      ? parseFloat(salaryLine.unit_count as any)
      : undefined,
  unit_sum:
    salaryLine?.symbol?.type !== 1
      ? parseFloat(salaryLine.unit_count as any)
      : undefined,
  retro_period:
    salaryLine?.retro_year && salaryLine?.retro_month
      ? moment([+salaryLine.retro_year, +salaryLine.retro_month - 1, 1])
      : null,
});

const formatSubmitValues = (
  values: SalaryEditFormValues
): Record<string, any> => ({
  ...values,
  unit_value: parseFloat(values.unit_value as any),
  unit_count: values.unit_count
    ? parseFloat(values.unit_count as any)
    : values.unit_sum !== undefined
    ? typeof values.unit_sum === "string"
      ? parseFloat(values.unit_sum)
      : values.unit_sum
    : undefined,
  retro_year: values.retro_period
    ? +moment(values.retro_period).format("YYYY")
    : undefined,
  retro_month: values.retro_period
    ? +moment(values.retro_period).format("M")
    : undefined,
});

/**
 * EditSalaryLineModal Component
 *
 * This component renders a modal dialog that allows users to edit individual salary line items.
 * It provides a form interface for updating salary-related information such as symbols, values,
 * retroactive periods, and order references.
 *
 * Features:
 * - Edits existing salary line entries for employees
 * - Handles different salary symbol types with dynamic form fields
 * - Supports retroactive period selection with date validation
 * - Integrates with employee orders when the accounting_tag feature is enabled
 * - Validates and formats input values before submission
 * - Automatically refreshes salary data after successful updates
 *
 * Form Fields:
 * 1. Salary Symbol - (Disabled) The classification of the salary component
 * 2. Unit Value - Monetary value per unit (shown only for type 1 symbols)
 * 3. Unit Count/Unit Sum - Number of units or total sum depending on symbol type
 * 4. Retroactive Period - Optional month/year for retroactive adjustments
 * 5. Order Reference - Optional link to an accounting order (when feature enabled)
 *
 * @example
 * ```tsx
 * <EditSalaryLineModal
 *   visible={isModalVisible}
 *   onHide={hideModal}
 *   salaryLine={selectedSalaryLine}
 * />
 * ```
 *
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Controls whether the modal is displayed
 * @param {Function} props.onHide - Callback function to close the modal
 * @param {SalaryExportLine} props.salaryLine - The salary line data to edit
 */
const EditSalaryLineModal: React.FC<EditSalaryLineModalProps> = ({
  onHide,
  visible,
  salaryLine,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const location = useLocation();
  const { branchId } = useParams<RouteParams>();
  const [form] = Form.useForm<SalaryEditFormValues>();
  const [formValues, setFormValues] = useState<SalaryEditFormValues>();
  // Redux state
  const updateLoading = useSelector(
    (state: RootState) => state.pendingActions["salaryLine/update"]
  );
  const activeBranch = useSelector(
    (state: RootState) => state.salary.activeBranch
  );
  const isAdmin = location.pathname.includes("admin");

  // Effects
  useEffect(() => {
    if (visible && salaryLine) {
      const {
        symbol_id,
        unit_value,
        unit_count,
        unit_sum,
        retro_month,
        retro_year,
        symbol,
        order_id,
      } = salaryLine;
      const initialValues = getInitialFormValues({
        symbol_id,
        unit_value,
        unit_count,
        unit_sum,
        order_id,
        retro_month,
        retro_year,
        symbol,
      });
      form.setFieldsValue(initialValues);
      setFormValues(initialValues);
    }
  }, [salaryLine, visible, form]);

  // Handlers
  const handleValuesChange = (
    changedValues: Partial<SalaryEditFormValues>,
    allValues: SalaryEditFormValues
  ) => {
    setFormValues(allValues);
    if ("retro_period" in changedValues) {
      form.setFieldValue("order_id", null);
    }
  };

  const handleSubmit = async (values: SalaryEditFormValues) => {
    const params = {
      branchId: isAdmin ? activeBranch : branchId,
      employeeId: salaryLine.employee_id,
      id: salaryLine.id,
      salaryLine: formatSubmitValues(values),
    };

    try {
      // Cast the result to the correct type with meta property
      const result = (await dispatch(
        updateRecord(params)
      )) as unknown as ThunkActionResponse;

      if (result.meta.requestStatus === "fulfilled") {
        dispatch(loadSalaryLines(isAdmin, salaryLine.employee_id));
        onHide();
      }
    } catch (err) {
      console.error("Failed to update salary line:", err);
    }
  };

  const disabledDate = (current: Moment | null): boolean => {
    if (!current) return false;
    const activeDate = moment([+salaryLine?.year, +salaryLine?.month - 1, 1]);
    return current > activeDate.endOf("month");
  };

  // Form items configuration
  interface FormItemConfig {
    key: number;
    component: React.ReactNode;
  }

  const formItems: FormItemConfig[] = [
    {
      key: 1,
      component: (
        <SettingsDataSelect
          name="symbol_id"
          category="salary"
          settingsdataName="system_tables/salary/salary_symbols"
          paramName="symbol"
          required
          disabled
        />
      ),
    },
    ...(salaryLine?.symbol?.type === 1
      ? [
          {
            key: 2,
            component: (
              <InputItem
                name="unit_value"
                category="salary"
                twoDigitsNumbers
                onlyPositiveNumbers
              />
            ),
          },
        ]
      : []),
    {
      key: 3,
      component: (
        <InputItem
          name={salaryLine?.symbol?.type === 1 ? "unit_count" : "unit_sum"}
          category="salary"
          required
          twoDigitsNumbers
          onlyPositiveNumbers
        />
      ),
    },
    {
      key: 4,
      component: (
        <DatePickerItem
          name="retro_period"
          category="salary"
          disabledDate={disabledDate}
          defaultPickerValue={undefined}
          picker="month"
          format="MM/YYYY"
        />
      ),
    },
    ...(isAllowedFeature("accounting_tag")
      ? [
          {
            key: 5,
            component: (
              <EmployeeEffectiveOrderSelect
                name="order_id"
                category="salary"
                required
                identfierId="order_id"
                staleTime={0}
                params={{
                  salaryLine: salaryLine,
                  branchId: salaryLine?.branch_id,
                  employeeId: salaryLine?.employee_id,
                  query: {
                    month: formValues?.retro_period
                      ? moment(formValues.retro_period).format("M")
                      : salaryLine?.month,
                    year: formValues?.retro_period
                      ? moment(formValues.retro_period).format("YYYY")
                      : salaryLine?.year,
                  },
                }}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width="90%"
      style={MODAL_STYLES.wrapper}
      styles={{ body: MODAL_STYLES.body }}
      open={visible}
      destroyOnClose
    >
      <div>
        <div className="header" style={MODAL_STYLES.header}>
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={onHide}
          />
        </div>
        <Title style={MODAL_STYLES.title}>
          {intl.formatMessage({ id: "app.salary.edit_salary_line" })}
        </Title>
        <div
          style={{ display: "flex", justifyContent: "center", width: "100%" }}
        >
          <Form
            {...FORM_LAYOUT}
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
            form={form}
            style={MODAL_STYLES.form}
          >
            <ChildrenAnimationWrapper>
              <Row>
                {formItems.map(({ key, component }) => (
                  <Col key={key} span={24} style={{ padding: "0 20px" }}>
                    <AnimationChildWrapper itemKey={key}>
                      {component}
                    </AnimationChildWrapper>
                  </Col>
                ))}
                <Col span={24}>
                  <FormActions
                    resource="employees"
                    action="editor"
                    hideDelete
                    hideDecline
                    mode="edit"
                    saveLoading={updateLoading}
                  />
                </Col>
              </Row>
            </ChildrenAnimationWrapper>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default EditSalaryLineModal;
